import React, { useState } from 'react';
import {
  ArrowRight, ShieldCheck, TrendingUp, FileText, Check, Send, CalendarCheck,
  LayoutGrid, FolderLock, Activity, Handshake, Users, Award,
  Sparkles, Star, Quote,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import Marquee from 'react-fast-marquee';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/* ─── DATA ─── */
const metrics = [
  { value: '100+', label: 'Startups Backed' },
  { value: '₹100Cr+', label: 'Funds Raised' },
  { value: '10k+', label: 'Filings Done' },
  { value: '99.95%', label: 'On-Time Filing' },
];

const complianceItems = ['Accounting & Book-keeping', 'GST Returns', 'TDS Returns', 'Income Tax / ITR', 'ROC & Secretarial', 'Statutory & Tax Audit'];

const portalFeatures = [
  { icon: LayoutGrid, title: 'Task Boards', desc: 'Every filing, its owner and stage — live.' },
  { icon: CalendarCheck, title: 'Compliance Calendar', desc: 'Never miss a due date again.' },
  { icon: FolderLock, title: 'Secure Document Vault', desc: 'All your records in one safe place.' },
  { icon: Activity, title: 'Real-Time Filing Status', desc: 'Know exactly where things stand.' },
];

const steps = [
  { num: '01', title: 'Share your stage', desc: 'Tell us where you are — pre-revenue, raising, or scaling. We map exactly what you need.' },
  { num: '02', title: 'We run the back-office', desc: 'Filings, diligence, audits and advisory handled by a dedicated team, tracked in your portal.' },
  { num: '03', title: 'Stay fundraise-ready', desc: 'Investor-grade books, on-time filings and proactive advice — so you keep building.' },
];

const pricing = [
  {
    name: 'Bootstrapped', tag: 'Early stage', price: 'Custom',
    desc: 'The essentials to stay clean and compliant.',
    features: ['Core compliance (GST + TDS)', 'Annual ROC filing', 'Quarterly GST returns', 'Client portal access', 'Email support'],
    featured: false,
  },
  {
    name: 'Seed / Series A', tag: 'Most popular', price: 'Custom',
    desc: 'Full-stack finance for companies raising and scaling.',
    features: ['Everything in Bootstrapped', 'Full-stack compliance', 'Monthly MIS & reporting', 'VC due-diligence prep', 'Dedicated CA + CS', 'Priority support'],
    featured: true,
  },
  {
    name: 'Growth / M&A', tag: 'Scale up', price: 'Custom',
    desc: 'Strategic merchant banking and deal support.',
    features: ['Everything in Seed / Series A', 'M&A & transaction advisory', 'Valuation & structuring', 'FEMA / cross-border', 'ESOP structuring'],
    featured: false,
  },
];

const credentials = [
  'Chartered Accountants', 'Company Secretaries', 'Cost & Management Accountants', 'Registered Valuers', 'SEBI-Reg. Merchant Bankers',
];

const testimonials = [
  { name: 'Rajesh Patel', company: 'NovaTech Solutions', text: 'Growartha took over our compliance when we were drowning in deadlines. Haven\'t missed a single filing since.' },
  { name: 'Anita Sharma', company: 'GreenLeaf Exports', text: 'Their portal and real-time tracking changed everything. We always know exactly where things stand.' },
  { name: 'Vikram Mehta', company: 'UrbanScale Studio', text: 'Compliance was my biggest anxiety as a founder. Growartha made it feel effortless.' },
  { name: 'Priya Nair', company: 'Finbridge Capital', text: 'Their diligence prep got us through our Series A data room without a single red flag.' },
  { name: 'Arjun Desai', company: 'CloudNine SaaS', text: 'We needed investor-ready financials fast. Growartha delivered flawlessly under tight deadlines.' },
];

const faqs = [
  { q: 'How does the equity-friendly fee structure work?', a: 'For high-potential early-stage startups, we structure fees as a blend of cash and equity (or sweat-equity). You get premium CA, CS and merchant-banking support without burning through your runway.' },
  { q: 'What exactly is covered under Compliance Services?', a: 'Our full-stack compliance covers accounting & book-keeping, GST, TDS, Income Tax (ITR), ROC/secretarial filings and statutory + tax audits — managed end-to-end from a single portal.' },
  { q: 'Can you help us get fundraise-ready?', a: 'Yes. Our Startup Funding Consultancy covers financial models, valuations, pitch support and investor readiness, while our Due Diligence Assistance prepares your books and data room for institutional scrutiny.' },
  { q: 'How is Growartha different from a traditional CA?', a: 'We pair deep CA expertise with a tech-enabled client portal — real-time task boards, compliance calendars, document vault and reminders — so you get proactive, year-round support, not just year-end filing.' },
  { q: 'What professionals are on your team?', a: 'Chartered Accountants, Company Secretaries, Cost & Management Accountants, Registered Valuers and SEBI-registered Merchant Bankers — comprehensive coverage under one roof.' },
  { q: 'Do you only serve Indian startups?', a: 'Our core expertise is the Indian regulatory landscape, including flipped and cross-border structures. We work with startups from pre-revenue through Series B and beyond.' },
];

const TEAM_IMG = 'https://images.unsplash.com/photo-1758691736975-9f7f643d178e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBtb2Rlcm4lMjBidXNpbmVzcyUyMHRlYW0lMjBzdGFuZGluZ3xlbnwwfHx8fDE3OTE3NDM5NTJ8MA&ixlib=rb-4.1.0&q=85';

/* ─── SMALL BUILDING BLOCKS ─── */
const Overline = ({ children, light }) => (
  <p className={`text-xs font-bold tracking-[0.22em] uppercase font-body ${light ? 'text-[#14B8A6]' : 'text-[#0f766e]'}`}>{children}</p>
);

const BrowserFrame = ({ src, alt, label = 'app.growartha.com', children, className = '' }) => (
  <div className={`rounded-xl overflow-hidden border border-[#E7E5E4] bg-white shadow-xl ${className}`}>
    <div className="flex items-center gap-1.5 px-4 h-9 bg-[#F3F0EA] border-b border-[#E7E5E4]">
      <span className="h-2.5 w-2.5 rounded-full bg-[#E07856]/70" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#E0B256]/70" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#14B8A6]/70" />
      <span className="ml-3 h-5 flex-1 max-w-[240px] rounded bg-white/70 border border-[#E7E5E4] flex items-center px-2 text-[9px] text-muted-foreground font-body">{label}</span>
    </div>
    {children ? children : <img src={src} alt={alt} className="w-full h-auto block" loading="lazy" />}
  </div>
);

/* ─── COMPONENT ─── */
const HomePage = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', message: '' });
  const [sending, setSending] = useState(false);
  const [checklistEmail, setChecklistEmail] = useState('');
  const [downloading, setDownloading] = useState(false);
  const EMPTY_QUOTE = { name: '', email: '', phone: '', turnover: '', invoices: '', employees: '', vendor_invoices: '', description: '', website: '' };
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quotePlan, setQuotePlan] = useState('');
  const [quote, setQuote] = useState(EMPTY_QUOTE);
  const [quoteSending, setQuoteSending] = useState(false);

  const openQuote = (plan) => { setQuotePlan(plan); setQuote(EMPTY_QUOTE); setQuoteOpen(true); };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    if (!quote.name || !quote.email) {
      toast.error('Please enter your name and email');
      return;
    }
    setQuoteSending(true);
    try {
      const notes = [
        `Plan: ${quotePlan}`,
        `Expected turnover (cr): ${quote.turnover || '-'}`,
        `Invoices / month: ${quote.invoices || '-'}`,
        `Employees: ${quote.employees || '-'}`,
        `Vendor invoices / month: ${quote.vendor_invoices || '-'}`,
        `Website: ${quote.website || '-'}`,
        `About: ${quote.description || '-'}`,
      ].join('\n');
      const res = await fetch(`${API}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: quote.name, email: quote.email, phone: quote.phone, service_interested: `Quote — ${quotePlan}`, notes }),
      });
      if (res.ok) {
        toast.success('Thanks! We\'ll send your custom quote within 24 hours.');
        setQuoteOpen(false);
      } else {
        toast.error('Could not submit. Please try again.');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setQuoteSending(false);
    }
  };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const handleChecklist = async (e) => {
    e.preventDefault();
    if (!checklistEmail || !/\S+@\S+\.\S+/.test(checklistEmail)) {
      toast.error('Please enter a valid email');
      return;
    }
    setDownloading(true);
    try {
      await fetch(`${API}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: checklistEmail.split('@')[0], email: checklistEmail, service_interested: 'Checklist Download', notes: 'Downloaded the Startup Compliance Checklist' }),
      });
      window.open(`${API}/api/resources/startup-compliance-checklist`, '_blank');
      toast.success('Your checklist is downloading — check your browser!');
      setChecklistEmail('');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success('Message sent! We\'ll get back to you shortly.');
        setForm({ name: '', email: '', phone: '', company: '', message: '' });
      } else {
        toast.error('Failed to send. Please try again.');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="overflow-hidden bg-[#FDFBF7]">
      {/* ═══ HERO ═══ */}
      <section className="relative bg-[#042F2E] pt-32 pb-20 lg:pt-40 lg:pb-28" data-testid="hero-section">
        {/* layered glow */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-32 -right-24 h-[32rem] w-[32rem] rounded-full bg-[#14B8A6]/15 blur-3xl" />
          <div className="absolute bottom-0 left-1/4 h-80 w-80 rounded-full bg-[#14B8A6]/10 blur-3xl" />
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
        </div>

        <div className="container-grid relative">
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            {/* Left */}
            <div className="lg:col-span-7 reveal-up">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-[#5eead4] font-body">
                <Sparkles className="h-3.5 w-3.5" /> CA · CS · CMA · Merchant Banking
              </span>
              <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08] text-white font-heading">
                Scale without the<br className="hidden sm:block" /> back-office <span className="text-[#14B8A6] italic">friction.</span>
              </h1>
              <p className="mt-6 max-w-xl text-base sm:text-lg text-white/70 leading-relaxed font-body">
                The financial operating system for modern Indian startups — from GST, TDS and ROC to due diligence and fundraise prep, all run by experts and tracked in a real-time client portal.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Button onClick={() => scrollTo('contact')} data-testid="hero-book-cta"
                  className="group bg-[#14B8A6] hover:bg-[#0fa093] text-[#042F2E] rounded-full h-12 px-8 font-body font-semibold text-sm shadow-lg shadow-[#14B8A6]/20 transition-colors">
                  Book a Free Consultation
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button onClick={() => scrollTo('services')} variant="outline" data-testid="hero-services-cta"
                  className="rounded-full h-12 px-8 font-body font-semibold text-sm border-white/25 bg-transparent text-white hover:bg-white/10 hover:border-white/50 transition-colors">
                  Explore Services
                </Button>
              </div>
              <p className="mt-6 text-xs text-white/40 font-body">Trusted by 100+ founders · Equity-friendly fees available</p>
            </div>

            {/* Right — floating portal preview */}
            <div className="lg:col-span-5 reveal-up" style={{ animationDelay: '120ms' }}>
              <div className="relative">
                <div className="absolute -inset-4 rounded-3xl bg-[#14B8A6]/10 blur-2xl" />
                <div className="relative rounded-2xl border border-white/10 bg-white/[0.06] backdrop-blur-xl p-4 shadow-2xl">
                  <div className="flex items-center justify-between px-1 pb-3">
                    <span className="text-[11px] font-semibold tracking-wide text-white/80 font-body">Compliance Dashboard</span>
                    <span className="text-[10px] text-[#5eead4] font-body">● Live</span>
                  </div>
                  <div className="space-y-2.5">
                    {[
                      { t: 'GSTR-3B · September', s: 'Filed', c: 'text-[#5eead4] bg-[#14B8A6]/15' },
                      { t: 'TDS Return · 26Q', s: 'In Review', c: 'text-[#E0B256] bg-[#E0B256]/15' },
                      { t: 'ROC · Annual Filing', s: 'On Track', c: 'text-white/70 bg-white/10' },
                      { t: 'Due Diligence Pack', s: 'Ready', c: 'text-[#5eead4] bg-[#14B8A6]/15' },
                    ].map((r) => (
                      <div key={r.t} className="flex items-center justify-between rounded-lg bg-[#042F2E]/40 border border-white/5 px-3.5 py-3">
                        <span className="text-xs text-white/85 font-body">{r.t}</span>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full font-body ${r.c}`}>{r.s}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    {[['24', 'This month'], ['100%', 'On time'], ['0', 'Penalties']].map(([v, l]) => (
                      <div key={l} className="rounded-lg bg-white/5 px-2 py-2.5 text-center">
                        <p className="text-sm font-bold text-white font-heading">{v}</p>
                        <p className="text-[9px] text-white/45 font-body mt-0.5">{l}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ METRICS BAND ═══ */}
      <section className="border-y border-[#E7E5E4] bg-[#FDFBF7]" data-testid="metrics-section">
        <div className="container-grid py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-6">
            {metrics.map((m) => (
              <div key={m.label} className="text-center md:text-left md:border-l md:border-[#E7E5E4] md:first:border-l-0 md:pl-6 first:pl-0">
                <p className="text-3xl sm:text-4xl font-semibold text-[#042F2E] font-heading tracking-tight">{m.value}</p>
                <p className="text-xs text-muted-foreground mt-1 font-body tracking-wide uppercase">{m.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SERVICES — BENTO ═══ */}
      <section id="services" className="py-20 lg:py-28 bg-[#F3F0EA]" data-testid="services-section">
        <div className="container-grid">
          <div className="max-w-2xl">
            <Overline>What we do</Overline>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground font-heading leading-[1.15]">
              Everything a growing startup needs. Nothing it doesn't.
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Due Diligence — tall */}
            <div className="md:col-span-5 md:row-span-2 group relative overflow-hidden rounded-xl bg-[#042F2E] p-8 lg:p-10 text-white shadow-sm hover:shadow-md transition-shadow" data-testid="service-due-diligence">
              <span className="absolute -right-4 -top-6 text-[8rem] font-semibold text-white/[0.05] font-heading select-none">01</span>
              <div className="relative">
                <div className="h-12 w-12 rounded-xl bg-[#14B8A6]/20 flex items-center justify-center">
                  <ShieldCheck className="h-6 w-6 text-[#5eead4]" />
                </div>
                <h3 className="mt-6 text-2xl lg:text-3xl font-medium font-heading tracking-tight">Due Diligence Assistance</h3>
                <p className="mt-4 text-white/70 font-body leading-relaxed">
                  Investor-grade diligence. We prepare your books, cap table and data room to survive institutional scrutiny — for M&A, VC rounds and legal audits.
                </p>
                <ul className="mt-6 space-y-2.5">
                  {['Data room readiness', 'Financial & tax diligence', 'Cap table clean-up', 'Red-flag remediation'].map((x) => (
                    <li key={x} className="flex items-center gap-2.5 text-sm text-white/80 font-body">
                      <Check className="h-4 w-4 text-[#5eead4] flex-shrink-0" /> {x}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Funding — wide */}
            <div className="md:col-span-7 group relative overflow-hidden rounded-xl bg-white border border-[#E7E5E4] p-8 lg:p-10 shadow-sm hover:shadow-md transition-shadow" data-testid="service-funding">
              <span className="absolute -right-2 -top-6 text-[8rem] font-semibold text-[#042F2E]/[0.04] font-heading select-none">02</span>
              <div className="relative flex flex-col sm:flex-row sm:items-start gap-6">
                <div className="h-12 w-12 rounded-xl bg-[#14B8A6]/10 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="h-6 w-6 text-[#0f766e]" />
                </div>
                <div>
                  <h3 className="text-2xl lg:text-3xl font-medium font-heading tracking-tight text-foreground">Startup Funding Consultancy</h3>
                  <p className="mt-3 text-muted-foreground font-body leading-relaxed">
                    From pitch deck to term sheet. Valuation, financial models and investor readiness — we sit on your side of the table.
                  </p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {['Financial modelling', 'Valuation', 'Pitch & deck support', 'Term-sheet advisory'].map((x) => (
                      <span key={x} className="text-xs font-medium font-body px-3 py-1.5 rounded-full bg-[#F3F0EA] text-foreground/70 border border-[#E7E5E4]">{x}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Compliance — wide */}
            <div className="md:col-span-7 group relative overflow-hidden rounded-xl bg-white border border-[#E7E5E4] p-8 lg:p-10 shadow-sm hover:shadow-md transition-shadow" data-testid="service-compliance">
              <span className="absolute -right-2 -top-6 text-[8rem] font-semibold text-[#042F2E]/[0.04] font-heading select-none">03</span>
              <div className="relative">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-xl bg-[#14B8A6]/10 flex items-center justify-center flex-shrink-0">
                    <FileText className="h-6 w-6 text-[#0f766e]" />
                  </div>
                  <div>
                    <h3 className="text-2xl lg:text-3xl font-medium font-heading tracking-tight text-foreground">Full-Stack Compliance Services</h3>
                    <p className="mt-3 text-muted-foreground font-body leading-relaxed">
                      End-to-end management of your entire compliance stack — one team, one portal, zero missed deadlines.
                    </p>
                  </div>
                </div>
                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                  {complianceItems.map((x) => (
                    <div key={x} className="flex items-center gap-2.5 rounded-lg bg-[#F3F0EA]/60 px-4 py-3 text-sm font-body text-foreground/80">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#14B8A6] flex-shrink-0" /> {x}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ PLATFORM / PORTAL SHOWCASE ═══ */}
      <section id="platform" className="py-20 lg:py-28 bg-[#FDFBF7]" data-testid="platform-section">
        <div className="container-grid">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Overline>The platform</Overline>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground font-heading leading-[1.15]">
                Your entire back-office. In one dashboard.
              </h2>
              <p className="mt-5 text-base sm:text-lg text-muted-foreground font-body leading-relaxed">
                No more scattered email threads or chasing your CA. The Growartha client portal gives you real-time visibility into every filing, deadline and document.
              </p>
              <div className="mt-8 grid sm:grid-cols-2 gap-5">
                {portalFeatures.map((f) => (
                  <div key={f.title} className="flex items-start gap-3.5">
                    <div className="h-10 w-10 rounded-lg bg-[#14B8A6]/10 flex items-center justify-center flex-shrink-0">
                      <f.icon className="h-5 w-5 text-[#0f766e]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold font-body text-foreground">{f.title}</p>
                      <p className="text-xs text-muted-foreground font-body mt-1 leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-6 rounded-3xl bg-[#042F2E]/[0.03] blur-2xl" />
              <BrowserFrame src="/portal-top.png" alt="Growartha client portal — compliance dashboard" label="app.growartha.com/dashboard" className="relative" />
              <div className="absolute -bottom-6 -left-6 hidden sm:block rounded-xl bg-[#042F2E] text-white px-5 py-4 shadow-xl">
                <p className="text-2xl font-semibold font-heading">0</p>
                <p className="text-[10px] uppercase tracking-wider text-white/60 font-body">Missed deadlines</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ EQUITY-FRIENDLY HIGHLIGHT ═══ */}
      <section className="py-16 lg:py-20 bg-[#042F2E] relative overflow-hidden" data-testid="equity-section">
        <div className="pointer-events-none absolute -right-20 -top-20 h-80 w-80 rounded-full bg-[#14B8A6]/10 blur-3xl" />
        <div className="container-grid relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <span className="inline-flex items-center gap-2 text-[#5eead4]"><Handshake className="h-5 w-5" /><Overline light>Equity-friendly fees</Overline></span>
              <h2 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-white font-heading leading-tight">
                Great CAs shouldn't cost you your runway.
              </h2>
              <p className="mt-4 max-w-2xl text-white/70 font-body leading-relaxed">
                For high-potential early-stage startups, we structure fees as a blend of cash and equity — so you get premium CA, CS and merchant-banking expertise without burning precious capital.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Button onClick={() => scrollTo('contact')} data-testid="equity-cta"
                className="group bg-[#14B8A6] hover:bg-[#0fa093] text-[#042F2E] rounded-full h-12 px-8 font-body font-semibold text-sm transition-colors">
                Discuss your fee model
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ PRICING ═══ */}
      <section id="pricing" className="py-20 lg:py-28 bg-[#F3F0EA]" data-testid="pricing-section">
        <div className="container-grid">
          <div className="max-w-2xl">
            <Overline>Pricing</Overline>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground font-heading leading-[1.15]">
              Startup-friendly plans for every stage.
            </h2>
            <p className="mt-4 text-base text-muted-foreground font-body">Transparent, flexible engagements — scale your plan as you grow.</p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
            {pricing.map((p) => (
              <div key={p.name}
                className={`relative flex flex-col rounded-xl p-8 transition-all duration-300 ${p.featured
                  ? 'bg-[#042F2E] text-white shadow-xl md:-translate-y-3 ring-1 ring-[#14B8A6]/30'
                  : 'bg-white border border-[#E7E5E4] shadow-sm hover:shadow-md'}`}
                data-testid={`pricing-${p.name.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                {p.featured && (
                  <span className="absolute -top-3 left-8 rounded-full bg-[#14B8A6] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#042F2E] font-body">{p.tag}</span>
                )}
                {!p.featured && <span className="text-[11px] font-semibold uppercase tracking-wider text-[#0f766e] font-body">{p.tag}</span>}
                <h3 className={`mt-3 text-2xl font-medium font-heading tracking-tight ${p.featured ? 'text-white' : 'text-foreground'}`}>{p.name}</h3>
                <p className={`mt-2 text-sm font-body ${p.featured ? 'text-white/60' : 'text-muted-foreground'}`}>{p.desc}</p>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className={`text-3xl font-semibold font-heading ${p.featured ? 'text-white' : 'text-foreground'}`}>{p.price}</span>
                  <span className={`text-xs font-body ${p.featured ? 'text-white/50' : 'text-muted-foreground'}`}>/ tailored quote</span>
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className={`flex items-start gap-2.5 text-sm font-body ${p.featured ? 'text-white/85' : 'text-foreground/80'}`}>
                      <Check className={`h-4 w-4 mt-0.5 flex-shrink-0 ${p.featured ? 'text-[#5eead4]' : 'text-[#14B8A6]'}`} /> {f}
                    </li>
                  ))}
                </ul>
                <Button onClick={() => openQuote(p.name)} data-testid={`pricing-cta-${p.name.toLowerCase().replace(/[^a-z]/g, '-')}`}
                  className={`mt-8 w-full rounded-full h-11 font-body font-semibold text-sm transition-colors ${p.featured
                    ? 'bg-[#14B8A6] hover:bg-[#0fa093] text-[#042F2E]'
                    : 'bg-[#042F2E] hover:bg-[#042F2E]/90 text-white'}`}>
                  Get a quote
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section className="py-20 lg:py-28 bg-[#FDFBF7]" data-testid="how-it-works-section">
        <div className="container-grid">
          <div className="max-w-2xl">
            <Overline>How it works</Overline>
            <h2 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-foreground font-heading">Onboarding in three simple steps.</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <div key={s.num} className="relative">
                <span className="text-5xl font-semibold text-[#14B8A6]/30 font-heading">{s.num}</span>
                {i < steps.length - 1 && <span className="hidden md:block absolute top-6 left-20 right-0 h-px bg-[#E7E5E4]" />}
                <h3 className="mt-4 text-xl font-medium font-heading tracking-tight text-foreground">{s.title}</h3>
                <p className="mt-2.5 text-sm text-muted-foreground font-body leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ TEAM / CREDIBILITY ═══ */}
      <section id="team" className="py-20 lg:py-28 bg-[#F3F0EA]" data-testid="team-section">
        <div className="container-grid">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="absolute -inset-6 rounded-3xl bg-[#042F2E]/[0.04] blur-2xl" />
              <img src={TEAM_IMG} alt="The Growartha multi-disciplinary team" loading="lazy"
                className="relative rounded-xl border border-[#E7E5E4] shadow-xl w-full h-[420px] object-cover" />
              <div className="absolute -top-5 -right-5 hidden sm:flex items-center gap-2 rounded-xl bg-white border border-[#E7E5E4] px-4 py-3 shadow-lg">
                <Award className="h-5 w-5 text-[#14B8A6]" />
                <span className="text-xs font-semibold font-body text-foreground">15+ yrs experience</span>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <Overline>The experts behind the scenes</Overline>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground font-heading leading-[1.15]">
                Multi-disciplinary experts in your corner.
              </h2>
              <p className="mt-5 text-base sm:text-lg text-muted-foreground font-body leading-relaxed">
                Growartha brings Chartered Accountants, Company Secretaries, Cost & Management Accountants, Registered Valuers and ex-bankers under one roof — people who understand the Indian startup journey at every stage.
              </p>
              <div className="mt-7 flex flex-wrap gap-2.5">
                {credentials.map((c) => (
                  <span key={c} className="inline-flex items-center gap-1.5 text-xs font-medium font-body px-3.5 py-2 rounded-full bg-white border border-[#E7E5E4] text-foreground/75">
                    <Users className="h-3.5 w-3.5 text-[#14B8A6]" /> {c}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ TESTIMONIALS ═══ */}
      <section className="py-20 lg:py-24 bg-[#FDFBF7] overflow-hidden" data-testid="testimonials-section">
        <div className="container-grid">
          <div className="max-w-2xl mb-12">
            <Overline>Testimonials</Overline>
            <h2 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-foreground font-heading">Founders who stopped worrying about compliance.</h2>
          </div>
        </div>
        <Marquee gradient gradientColor="#FDFBF7" gradientWidth={80} speed={32} pauseOnHover>
          {testimonials.map((t) => (
            <div key={t.name} className="mx-3 w-[360px] rounded-xl bg-white border border-[#E7E5E4] p-7 shadow-sm">
              <Quote className="h-6 w-6 text-[#14B8A6]/40" />
              <p className="mt-4 text-sm text-foreground/80 font-body leading-relaxed">{t.text}</p>
              <div className="mt-5 flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-[#042F2E] text-white flex items-center justify-center text-xs font-semibold font-heading">
                  {t.name[0]}
                </div>
                <div>
                  <p className="text-sm font-semibold font-body text-foreground">{t.name}</p>
                  <p className="text-xs text-muted-foreground font-body">{t.company}</p>
                </div>
                <div className="ml-auto flex gap-0.5">
                  {[...Array(5)].map((_, i) => <Star key={i} className="h-3 w-3 fill-[#14B8A6] text-[#14B8A6]" />)}
                </div>
              </div>
            </div>
          ))}
        </Marquee>
      </section>

      {/* ═══ FAQ ═══ */}
      <section id="faq" className="py-20 lg:py-28 bg-[#F3F0EA]" data-testid="faq-section">
        <div className="container-grid">
          <div className="grid lg:grid-cols-12 gap-14">
            <div className="lg:col-span-5">
              <Overline>FAQ</Overline>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground font-heading leading-[1.15]">
                Questions founders ask us.
              </h2>
              <p className="mt-5 text-base text-muted-foreground font-body leading-relaxed">
                Can't find what you're looking for? <button onClick={() => scrollTo('contact')} className="text-[#0f766e] font-semibold hover:underline" data-testid="faq-contact-link">Talk to us →</button>
              </p>
            </div>
            <div className="lg:col-span-7">
              <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
                {faqs.map((faq, i) => (
                  <AccordionItem key={i} value={`item-${i}`} className="border-b border-[#E7E5E4]">
                    <AccordionTrigger className="text-left text-base font-medium font-body text-foreground hover:no-underline py-5" data-testid={`faq-trigger-${i}`}>
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground font-body leading-relaxed pb-5">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ LEAD MAGNET — CHECKLIST ═══ */}
      <section className="py-16 lg:py-20 bg-[#FDFBF7]" data-testid="checklist-section">
        <div className="container-grid">
          <div className="relative overflow-hidden rounded-2xl bg-[#042F2E] px-8 py-12 lg:px-14 lg:py-14">
            <div className="pointer-events-none absolute -right-16 -top-16 h-72 w-72 rounded-full bg-[#14B8A6]/15 blur-3xl" />
            <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)', backgroundSize: '26px 26px' }} />
            <div className="relative grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <span className="inline-flex items-center gap-2 text-[#5eead4]">
                  <FileText className="h-5 w-5" /><Overline light>Free resource</Overline>
                </span>
                <h2 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-white font-heading leading-tight">
                  Not ready to talk yet? Grab the checklist.
                </h2>
                <p className="mt-4 max-w-md text-white/70 font-body leading-relaxed">
                  Download our <span className="text-white font-medium">Startup Compliance Checklist</span> — every GST, TDS, ROC and fundraising to-do founders need, on one page.
                </p>
              </div>
              <div className="lg:justify-self-end w-full max-w-md">
                <form onSubmit={handleChecklist} className="flex flex-col sm:flex-row gap-3" data-testid="checklist-form">
                  <input
                    type="email"
                    value={checklistEmail}
                    onChange={(e) => setChecklistEmail(e.target.value)}
                    placeholder="you@startup.com"
                    className="flex-1 h-12 rounded-full bg-white/10 border border-white/20 px-5 text-sm text-white placeholder:text-white/40 font-body focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/50 focus:border-[#14B8A6] transition-all"
                    data-testid="checklist-email-input"
                  />
                  <Button type="submit" disabled={downloading}
                    className="group bg-[#14B8A6] hover:bg-[#0fa093] text-[#042F2E] rounded-full h-12 px-7 font-body font-semibold text-sm transition-colors whitespace-nowrap"
                    data-testid="checklist-download-btn">
                    {downloading ? 'Preparing…' : 'Download Free'}
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </form>
                <p className="mt-3 text-[11px] text-white/40 font-body">No spam. Just the checklist — and the occasional founder-friendly tip.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ CONTACT ═══ */}
      <section id="contact" className="py-20 lg:py-28 bg-[#FDFBF7]" data-testid="contact-section">
        <div className="container-grid">
          <div className="grid lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-5">
              <Overline>Book a free consultation</Overline>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground font-heading leading-[1.15]">
                Let's get your finances founder-proof.
              </h2>
              <p className="mt-5 text-base text-muted-foreground leading-relaxed font-body">
                Share a few details and we'll come back within 24 hours with a plan tailored to your startup's stage.
              </p>
              <div className="mt-10 space-y-5">
                {[
                  { label: 'Free initial consultation', sub: 'No commitment, no pressure' },
                  { label: 'Equity-friendly models available', sub: 'For early-stage startups' },
                  { label: 'Response within 24 hours', sub: 'Dedicated support from day one' },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-4">
                    <div className="h-8 w-8 rounded-full bg-[#14B8A6]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-4 w-4 text-[#14B8A6]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold font-body">{item.label}</p>
                      <p className="text-xs text-muted-foreground font-body mt-0.5">{item.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7">
              <form
                onSubmit={handleSubmit}
                className="p-8 sm:p-10 rounded-2xl bg-white border border-[#E7E5E4]"
                style={{ boxShadow: '8px 8px 24px rgba(4,47,46,0.05), -4px -4px 16px rgba(255,255,255,0.8)' }}
                data-testid="contact-form"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-2 font-body">Full Name *</label>
                    <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full h-11 rounded-xl border border-border bg-white px-4 text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/30 focus:border-[#14B8A6] transition-all"
                      placeholder="Your full name" data-testid="contact-name-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-2 font-body">Email *</label>
                    <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full h-11 rounded-xl border border-border bg-white px-4 text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/30 focus:border-[#14B8A6] transition-all"
                      placeholder="you@company.com" data-testid="contact-email-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-2 font-body">Phone</label>
                    <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="w-full h-11 rounded-xl border border-border bg-white px-4 text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/30 focus:border-[#14B8A6] transition-all"
                      placeholder="+91-XXXXXXXXXX" data-testid="contact-phone-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-2 font-body">Startup Name</label>
                    <input type="text" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                      className="w-full h-11 rounded-xl border border-border bg-white px-4 text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/30 focus:border-[#14B8A6] transition-all"
                      placeholder="Your startup name" data-testid="contact-company-input" />
                  </div>
                </div>
                <div className="mt-5">
                  <label className="block text-xs font-medium text-foreground mb-2 font-body">How can we help? *</label>
                  <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4}
                    className="w-full rounded-xl border border-border bg-white px-4 py-3 text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/30 focus:border-[#14B8A6] transition-all resize-none"
                    placeholder="Tell us about your startup and what you need..." data-testid="contact-message-input" />
                </div>
                <Button type="submit" disabled={sending}
                  className="mt-6 w-full sm:w-auto bg-[#042F2E] hover:bg-[#042F2E]/90 text-white rounded-full h-12 px-10 font-body font-semibold text-sm transition-colors"
                  data-testid="contact-submit-button">
                  {sending ? 'Sending...' : 'Book My Consultation'} <Send className="ml-2 h-4 w-4" />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ QUOTE DIALOG ═══ */}
      <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="quote-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight text-foreground">Get your custom quote</DialogTitle>
            <DialogDescription className="font-body">
              {quotePlan ? <>For the <span className="font-semibold text-[#0f766e]">{quotePlan}</span> plan. </> : null}
              Tell us about your startup and we'll tailor a proposal within 24 hours.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleQuoteSubmit} className="space-y-4" data-testid="quote-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5 font-body">Full Name *</label>
                <input type="text" value={quote.name} onChange={(e) => setQuote({ ...quote, name: e.target.value })}
                  className="w-full h-10 rounded-lg border border-border bg-white px-3 text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/30 focus:border-[#14B8A6]"
                  placeholder="Your name" data-testid="quote-name" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5 font-body">Email *</label>
                <input type="email" value={quote.email} onChange={(e) => setQuote({ ...quote, email: e.target.value })}
                  className="w-full h-10 rounded-lg border border-border bg-white px-3 text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/30 focus:border-[#14B8A6]"
                  placeholder="you@startup.com" data-testid="quote-email" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5 font-body">Phone</label>
                <input type="tel" value={quote.phone} onChange={(e) => setQuote({ ...quote, phone: e.target.value })}
                  className="w-full h-10 rounded-lg border border-border bg-white px-3 text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/30 focus:border-[#14B8A6]"
                  placeholder="+91-XXXXXXXXXX" data-testid="quote-phone" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5 font-body">Expected turnover (₹ crores)</label>
                <input type="number" min="0" step="0.1" value={quote.turnover} onChange={(e) => setQuote({ ...quote, turnover: e.target.value })}
                  className="w-full h-10 rounded-lg border border-border bg-white px-3 text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/30 focus:border-[#14B8A6]"
                  placeholder="e.g. 5" data-testid="quote-turnover" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5 font-body">Expected invoices / month</label>
                <input type="number" min="0" value={quote.invoices} onChange={(e) => setQuote({ ...quote, invoices: e.target.value })}
                  className="w-full h-10 rounded-lg border border-border bg-white px-3 text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/30 focus:border-[#14B8A6]"
                  placeholder="e.g. 120" data-testid="quote-invoices" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5 font-body">No. of Employees</label>
                <input type="number" min="0" value={quote.employees} onChange={(e) => setQuote({ ...quote, employees: e.target.value })}
                  className="w-full h-10 rounded-lg border border-border bg-white px-3 text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/30 focus:border-[#14B8A6]"
                  placeholder="e.g. 15" data-testid="quote-employees" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5 font-body">Vendor invoices / month</label>
                <input type="number" min="0" value={quote.vendor_invoices} onChange={(e) => setQuote({ ...quote, vendor_invoices: e.target.value })}
                  className="w-full h-10 rounded-lg border border-border bg-white px-3 text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/30 focus:border-[#14B8A6]"
                  placeholder="e.g. 40" data-testid="quote-vendor-invoices" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5 font-body">Website URL</label>
                <input type="text" value={quote.website} onChange={(e) => setQuote({ ...quote, website: e.target.value })}
                  className="w-full h-10 rounded-lg border border-border bg-white px-3 text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/30 focus:border-[#14B8A6]"
                  placeholder="https://yourstartup.com" data-testid="quote-website" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5 font-body">Brief description of your startup</label>
              <textarea rows={3} value={quote.description} onChange={(e) => setQuote({ ...quote, description: e.target.value })}
                className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm font-body resize-none focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/30 focus:border-[#14B8A6]"
                placeholder="What does your startup do?" data-testid="quote-description" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={quoteSending}
                className="w-full sm:w-auto bg-[#042F2E] hover:bg-[#042F2E]/90 text-white rounded-full h-11 px-8 font-body font-semibold text-sm transition-colors"
                data-testid="quote-submit">
                {quoteSending ? 'Submitting…' : 'Request Quote'} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomePage;
